function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-6 border-l-4 border-amber-500 pl-4 text-xl font-bold text-gray-900">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="mb-3 mt-8 text-lg font-bold text-gray-900">{children}</h3>;
}

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex border-b border-gray-200 py-5">
      <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">{label}</dt>
      <dd className="text-sm leading-relaxed text-gray-700">{children}</dd>
    </div>
  );
}

function CompanyInfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-gray-100 py-3">
      <dt className="mb-1 text-xs font-bold text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-800">{children}</dd>
    </div>
  );
}

export function CtoJobPostingPage() {
  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-4">
          <span className="text-xl font-bold tracking-tight text-amber-600">募集要項サンプル</span>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto max-w-[1100px] px-6 py-10">
        {/* Title */}
        <h1 className="mb-10 text-3xl font-bold text-gray-900">CTO候補</h1>

        {/* 2-column layout */}
        <div className="flex gap-12">
          {/* Left: Job content */}
          <div className="min-w-0 flex-1">
            {/* 採用情報 */}
            <section className="mb-12">
              <SectionHeading>採用情報</SectionHeading>

              <div className="flex border-b border-gray-200 py-5">
                <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">職務内容</dt>
                <dd className="text-sm leading-relaxed text-gray-700">
                  CTO候補として、自社SaaSプロダクト「DataFlow」の技術戦略策定からアーキテクチャ設計、エンジニアリング組織の構築・強化までをリードしていただきます。経営陣の一員として事業成長を技術面から牽引し、プロダクトの競争優位性を確立する役割を期待しています。
                </dd>
              </div>

              <SubHeading>具体的な業務</SubHeading>
              <div className="space-y-1 text-sm leading-relaxed text-gray-700">
                <p>・全社技術戦略の策定・推進（技術ロードマップ、アーキテクチャ方針）</p>
                <p>・SaaSプロダクト「DataFlow」のシステムアーキテクチャ設計・技術的意思決定</p>
                <p>・エンジニアリング組織の構築・採用・育成（現15名→30名体制へのスケール）</p>
                <p>・開発プロセスの最適化（CI/CD、コードレビュー体制、品質基準の策定）</p>
                <p>・セキュリティ・インフラ戦略の立案と実行</p>
                <p>・経営会議への参画、技術投資の意思決定</p>
                <p>・社外への技術発信（カンファレンス登壇、テックブログ、採用広報）</p>
              </div>

              <SubHeading>募集背景</SubHeading>
              <p className="text-sm leading-relaxed text-gray-700">
                当社はシリーズBの資金調達を完了し、プロダクトのエンタープライズ展開を加速しています。現在、エンジニアリング組織は15名規模ですが、今後1年で30名体制への拡大を計画しており、技術戦略と組織の両面をリードできるCTOを迎え入れることが急務となっています。これまでCEOが技術面も兼務してきましたが、事業規模の拡大に伴い、専任のCTOとして技術経営を担っていただける方を求めています。
              </p>

              <SubHeading>仕事の魅力</SubHeading>
              <div className="space-y-1 text-sm leading-relaxed text-gray-700">
                <p>・経営直下のポジションで、技術戦略から事業戦略まで幅広い意思決定に関与できる</p>
                <p>・シリーズBのグロースフェーズで、組織とプロダクトをゼロからスケールさせる経験</p>
                <p>・AI/MLを活用した次世代データプラットフォームの技術的挑戦</p>
                <p>・大手製造業・金融機関など、エンタープライズ顧客への技術提案</p>
                <p>・フルリモート＋フレックスで、パフォーマンスを最大化できる働き方</p>
              </div>

              <SubHeading>データ統合SaaSプロダクト：DataFlow</SubHeading>
              <p className="text-sm leading-relaxed text-gray-700">
                DataFlowは、企業のデータパイプライン構築・運用を効率化するSaaSプラットフォームです。ノーコードでのETL構築、リアルタイムデータ処理、AI/MLモデルの統合を一つのプラットフォームで実現します。現在、製造業・金融業を中心に50社以上の導入実績があり、ARR3億円を突破しています。
              </p>

              <SubHeading>当社について</SubHeading>
              <p className="text-sm leading-relaxed text-gray-700">
                株式会社テクノフューチャーは「データで未来をデザインする」をミッションに、2020年に設立されました。企業のDX推進を支援するデータプラットフォーム事業を展開し、設立4年でシリーズB（累計調達額20億円）を達成。製造業・金融業のエンタープライズ顧客を中心に急成長を続けています。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-gray-700">
                技術力と事業価値の両立を重視し、エンジニアが技術的挑戦を楽しみながらビジネスインパクトを生み出せる組織文化を大切にしています。
              </p>

              <SubHeading>ボードメンバーについて</SubHeading>
              <p className="text-sm leading-relaxed text-gray-700">
                元Google/メルカリ出身のCEOをはじめ、元大手コンサルファーム×MBAのCOO、シリーズAスタートアップのCFO経験者、データサイエンス領域の第一人者など、各分野のプロフェッショナルが経営陣に在籍しています。
              </p>
            </section>

            {/* 応募資格（必須） */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">応募資格（必須）</dt>
              <dd className="space-y-1 text-sm leading-relaxed text-gray-700">
                <p>以下のご経験</p>
                <p>・CTO、VPoE、またはテックリードとして技術組織を牽引した経験（3年以上）</p>
                <p>・Webアプリケーションのアーキテクチャ設計・実装経験（5年以上）</p>
                <p>・クラウドネイティブ環境（AWS/GCP/Azure）での大規模システム設計・運用経験</p>
                <p>・10名以上のエンジニアチームのマネジメント・採用経験</p>
                <p>・ビジネスサイドと連携した技術投資の意思決定経験</p>
              </dd>
            </div>

            {/* 応募資格（歓迎） */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">応募資格（歓迎）</dt>
              <dd className="space-y-1 text-sm leading-relaxed text-gray-700">
                <p>・SaaSプロダクトの0→1または1→10フェーズでのCTO経験</p>
                <p>・AI/ML基盤の設計・運用経験</p>
                <p>・OSS公開やコミュニティ活動の実績</p>
                <p>・エンタープライズ向けセキュリティ要件（SOC2、ISMSなど）への対応経験</p>
                <p>・グローバルチームでの開発経験</p>
                <p>・カンファレンス登壇、技術ブログ等の対外発信経験</p>
              </dd>
            </div>

            {/* 求める人物像 */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">求める人物像</dt>
              <dd className="space-y-1 text-sm leading-relaxed text-gray-700">
                <p>・技術と事業をつなぎ、経営的観点から意思決定ができる方</p>
                <p>・「作る」だけでなく「届ける」までを自分事として捉えられる方</p>
                <p>・不確実性の高い環境でも、仮説を立てて素早く実行に移せる方</p>
                <p>・チームメンバーの成長にコミットし、強い組織を作ることに情熱を持てる方</p>
                <p>・技術トレンドへの高い感度と、本質を見極める目を持つ方</p>
              </dd>
            </div>

            {/* 賃金 */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">賃金</dt>
              <dd className="text-sm leading-relaxed text-gray-700">
                <ul className="list-inside list-disc space-y-1">
                  <li>100万円/月〜（月40時間のみなし残業代込み）</li>
                  <li>年俸制</li>
                  <li>決算賞与あり（会社業績による）</li>
                  <li>ストック・オプション（評価に応じて）</li>
                </ul>
              </dd>
            </div>

            {/* 勤務地 */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">勤務地</dt>
              <dd className="text-sm leading-relaxed text-gray-700">
                東京都渋谷区神宮前六丁目23番4号 桑野ビル2F
                <br />
                ※フルリモート勤務可（月1回程度のオフサイトミーティングあり）
              </dd>
            </div>

            {/* 募集背景（詳細） */}
            <div className="flex border-b border-gray-200 py-5">
              <dt className="w-28 shrink-0 text-sm font-bold text-gray-700">募集背景</dt>
              <dd className="text-sm leading-relaxed text-gray-700">
                <p>
                  テクノフューチャーは「データで未来をデザインする」をミッションに、2020年に設立いたしました。
                </p>
                <p className="mt-3">
                  日本企業のDX推進は加速しているものの、データ活用基盤の構築には依然として多くの課題が残されています。特にエンタープライズ領域では、複雑なデータパイプラインの構築・運用に膨大な工数がかかり、本来注力すべきデータ分析やビジネス活用に十分なリソースを割けていない企業が多数存在します。
                </p>
                <p className="mt-3">
                  CEOの山田が大手IT企業でデータ基盤の構築に携わる中で、多くの企業がデータ活用の「前段階」で躓いている現実を目の当たりにしてきました。テクノフューチャーでは、データ分析やテクノロジーを活用しながら、企業のデータ活用を根本から変革するプラットフォーム「DataFlow」を開発いたしました。
                </p>
                <p className="mt-3">
                  直近で資金調達も実施し、急拡大を狙っている中で、DataFlowの価値を一緒に世の中へ届けていただけると幸いです。
                </p>
              </dd>
            </div>

            {/* 募集要項 */}
            <section className="mb-12 mt-12">
              <SectionHeading>募集要項</SectionHeading>

              <dl>
                <InfoRow label="Mission">
                  <ul className="list-inside list-disc space-y-1">
                    <li>Shape the Future</li>
                    <li>データで未来をデザインする</li>
                  </ul>
                </InfoRow>
                <InfoRow label="Vision">
                  <ul className="list-inside list-disc space-y-1">
                    <li>Data-Driven World</li>
                    <li>すべての企業にデータの力を</li>
                  </ul>
                </InfoRow>
                <InfoRow label="Value">
                  <ul className="list-inside list-disc space-y-1">
                    <li>Own It</li>
                    <li>Ship It</li>
                    <li>Learn It</li>
                  </ul>
                </InfoRow>
                <InfoRow label="勤務時間">フルフレックス勤務（1日実働8h、休憩1h）</InfoRow>
                <InfoRow label="昇給・賞与">
                  <ul className="list-inside list-disc space-y-1">
                    <li>年1回（7月）報酬改定</li>
                    <li>ストック・オプション（評価に応じて）</li>
                  </ul>
                </InfoRow>
                <InfoRow label="諸手当">
                  <ul className="list-inside list-disc space-y-1">
                    <li>リモートワーク手当（月3万円）</li>
                  </ul>
                </InfoRow>
                <InfoRow label="休日・休暇">
                  <ul className="list-inside list-disc space-y-1">
                    <li>土日祝日休み</li>
                    <li>年次有給休暇／慶弔休暇／年末年始休暇／産休、育休等</li>
                    <li>年間休日120日以上</li>
                    <li>入社時に有給休暇（10日）付与</li>
                  </ul>
                </InfoRow>
                <InfoRow label="福利厚生">
                  <ul className="list-inside list-disc space-y-1">
                    <li>社会保険・労働保険完備</li>
                    <li>通勤手当（上限5万円/月）</li>
                    <li>チームビルディング促進制度</li>
                    <li>書籍購入補助</li>
                    <li>お好きなPCの貸与（上限額あり）</li>
                    <li>技術カンファレンス参加費全額補助</li>
                    <li>副業OK</li>
                  </ul>
                </InfoRow>
              </dl>
            </section>
          </div>

          {/* Right Sidebar */}
          <aside className="hidden w-[300px] shrink-0 lg:block">
            <div className="sticky top-6 space-y-6">
              {/* Apply Button */}
              <button
                className="w-full rounded bg-amber-500 py-4 text-lg font-bold text-white transition-colors hover:bg-amber-600"
                disabled
                type="button"
              >
                応募する
              </button>
              <p className="-mt-4 text-center text-xs text-gray-400">※ これはサンプル求人です</p>

              {/* Company Info Card */}
              <div className="rounded border border-gray-200 bg-white p-5">
                <h4 className="mb-4 font-bold text-gray-900">会社情報</h4>
                <dl>
                  <CompanyInfoRow label="設立">2020年4月</CompanyInfoRow>
                  <CompanyInfoRow label="代表者">山田 太郎</CompanyInfoRow>
                  <CompanyInfoRow label="事業内容">
                    企業向けデータプラットフォームの開発・運営
                  </CompanyInfoRow>
                  <CompanyInfoRow label="従業員数">45名（業務委託含む）</CompanyInfoRow>
                  <CompanyInfoRow label="資本金">8億5,000万円（資本準備金含む）</CompanyInfoRow>
                  <CompanyInfoRow label="所在地">
                    東京都渋谷区神宮前六丁目23番4号 桑野ビル2F
                  </CompanyInfoRow>
                  <CompanyInfoRow label="企業サイトURL">
                    <span className="text-amber-600">https://techno-future.example.com</span>
                  </CompanyInfoRow>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Fixed bottom apply bar */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-[1100px] items-center justify-center px-6 py-3">
          <button
            className="rounded bg-amber-500 px-20 py-3 text-base font-bold text-white transition-colors hover:bg-amber-600"
            disabled
            type="button"
          >
            応募する
          </button>
        </div>
      </div>

      {/* Scroll to top button */}
      <button
        className="fixed bottom-20 right-6 z-40 flex h-12 w-12 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg transition-colors hover:bg-gray-50"
        onClick={() => globalThis.scrollTo({ top: 0, behavior: "smooth" })}
        type="button"
      >
        <svg
          className="h-5 w-5 text-gray-500"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <title>ページ上部へ</title>
          <path d="M5 15l7-7 7 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* Footer spacer for fixed bottom bar */}
      <div className="h-16" />
    </div>
  );
}
